// CTV-800: the staff Studio's copy for the shared generation ledger
// (./ledger.tsx). Same four rows and four pills as the buyer wizard, worded
// for a staff member making a commercial for a client rather than a buyer
// making one for their own campaign. The neutral accent stands in for the
// station gradient: the Studio belongs to no station.

import type { WaymarkStage } from './context';
import type { LedgerRowSpec, StagePillSpec } from './ledger';

/** slate-900: neutral, and what the Studio's own chrome already uses. */
export const STUDIO_ACCENT = '#0f172a';

export const STUDIO_LEDGER_ROWS: LedgerRowSpec[] = [
  {
    key: 'analyzing',
    doneLabel: (host) => `Read ${host}`,
    completedAtStage: 'drafting',
    currentTitle: (host) => `Reading ${host}`,
    currentDesc: () => 'Waymark is grabbing the logo, brand colors, photos and copy to build from.',
    currentHint: '≈8 min total',
    todoLabel: 'Reading the website',
    todoHint: 'about 30s',
  },
  {
    key: 'drafting',
    doneLabel: () => 'First draft written and rendered',
    completedAtStage: 'customize',
    currentTitle: () => 'Writing and rendering the first draft',
    currentDesc: () =>
      'Waymark reports no progress while it works, so there is nothing to count down.',
    currentHint: '≈7 min total',
    todoLabel: 'Writing and rendering the first draft',
    todoHint: 'about 4 min',
  },
  {
    key: 'customize',
    doneLabel: () => 'Customized in the editor',
    completedAtStage: 'finalizing',
    currentTitle: () => 'Customize it for the client',
    currentDesc: () => 'Open the editor, change the words and pictures, then click Finish.',
    currentHint: 'waiting on you',
    todoLabel: 'You customize the words and pictures',
    todoHint: 'your pace',
  },
  {
    key: 'finalizing',
    doneLabel: () => 'Broadcast file rendered',
    completedAtStage: 'ready',
    currentTitle: () => 'Making the broadcast file',
    currentDesc: () =>
      'Waymark is rendering the file you just finished. Keep this tab open: the render is recorded when this page hears back.',
    currentHint: null,
    todoLabel: 'Broadcast file rendered',
    todoHint: 'about 3 min',
  },
];

export const STUDIO_STAGE_PILLS: Record<WaymarkStage, StagePillSpec> = {
  analyzing: { stage: 1, label: 'Analyzing', estimate: '~30s', pulse: true },
  drafting: { stage: 2, label: 'Drafting', estimate: '~4 min', pulse: true },
  customize: { stage: 3, label: 'You customize', estimate: null, pulse: false },
  finalizing: { stage: 4, label: 'Rendering', estimate: '~3 min', pulse: true },
  ready: { stage: 4, label: 'Done', estimate: null, pulse: false, done: true },
};
